import { createStyles, HawkinsGlobalClass, makeStyles } from '@hawkins/styles';
import { COLLAPSE_ANIMATION_DURATION, COLLAPSE_ANIMATION_TIMING_FN } from '../transitions/collapse.styles';
export function accordionSummaryStyles(theme) {
    var _a, _b, _c, _d;
    var hawkins = theme.hawkins, hawkinsUtils = theme.hawkinsUtils, hawkinsVariables = theme.hawkinsVariables;
    var borderBottomWidth = parseInt(hawkins.ui.borderWidthRegular, 10);
    var minHeightCompact = hawkinsVariables.sizings.size3;
    var minHeightLarge = hawkinsVariables.sizings.size6;
    var minHeightRegular = hawkinsVariables.sizings.size5;
    var padding = hawkinsVariables.spacings.space3;
    var toggleIconContainerDim = theme.hawkinsVariables.sizings.size4;
    var toggleIconContainerDimCompact = theme.hawkinsVariables.sizings.size2;
    var toggleIconSkeletonLoaderDim = (theme.hawkinsVariables.sizingValues.size1 * 5) / 4;
    var toggleIconSkeletonLoaderDimCompact = hawkinsVariables.sizings.size1;
    return createStyles({
        /** Class name of the element containing the additional action. */
        additionalActionContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginLeft: 'auto',
        },
        /** Class name of the element containing the main content. */
        content: {},
        /** Class name of the root element. */
        root: (_a = {
                alignItems: 'center',
                borderBottomColor: hawkins.ui.borderColorXlight,
                borderBottomStyle: 'solid',
                borderBottomWidth: borderBottomWidth,
                display: 'flex',
                gap: hawkinsVariables.spacings.space2,
                minHeight: minHeightRegular,
                padding: "".concat(hawkinsVariables.spacings.space0, " ").concat(hawkinsVariables.spacings.space3)
            },
            _a["&.".concat(HawkinsGlobalClass.FOCUSABLE)] = (_b = {},
                _b[hawkinsUtils.getFocusRingedElementSelector('&', '::after')] = { bottom: -1 * borderBottomWidth },
                _b),
            _a),
        /** Class name of the root element when the size variant is "auto". */
        rootAuto: {
            minHeight: 'auto',
            '&$rootWithTitleAndSubtitle': { minHeight: 'auto' },
        },
        /** Class name of the root element when component is compact. */
        rootCompact: {
            minHeight: minHeightCompact,
            '&$rootWithTitleAndSubtitle': { minHeight: hawkinsVariables.sizings.size4 },
        },
        /** Class name of the root element when the component is disabled. */
        rootDisabled: {
            cursor: 'not-allowed',
            '& $toggleIcon': { color: hawkins.icon.fillXlight },
        },
        /** Class name of the root element when the `Accordion` is expanded. */
        rootExpanded: {
            backgroundColor: hawkins.highlight.backgroundColorRegular,
            '& $toggleIcon': { transform: 'rotate(90deg)' },
        },
        /** Class name of the root element when component is large. */
        rootLarge: {
            minHeight: minHeightLarge,
            padding: "".concat(hawkinsVariables.spacings.space1, " ").concat(padding),
            '&$rootWithTitleAndSubtitle': {
                minHeight: hawkinsVariables.sizingValues.size4 * 2,
                padding: padding,
            },
        },
        /**
         * Class name of the root element when the entire `AccordianSummary` is clickable to toggle the `Accordion`
         * and the component is not disabled.
         */
        rootToggleable: (_c = {
                cursor: 'pointer'
            },
            _c[hawkinsUtils.getHoveredElementSelector('&')] = { backgroundColor: hawkins.state.backgroundColorHover },
            _c),
        /**
         * Class name of the root element when the component has a title and subtitle.
         */
        rootWithTitleAndSubtitle: {
            minHeight: hawkinsVariables.sizings.size6,
        },
        /** Class name of the element containing all the skeleton loaders. */
        skeletonLoadersContainer: {
            alignItems: 'center',
            columnGap: hawkinsVariables.spacings.space5,
            display: 'grid',
            gridTemplate: "\n        \"title toggle-icon\" min-content\n        \"subtitle .\" min-content\n        / 1fr min-content\n      ",
            padding: padding,
            rowGap: hawkinsVariables.spacingValues.space0,
        },
        /** Class name of the skeleton loaders container when the component is compact. */
        skeletonLoadersContainerCompact: {
            minHeight: minHeightCompact,
        },
        /** Class name of the skeleton loaders container when the component is large. */
        skeletonLoadersContainerLarge: {
            minHeight: minHeightLarge,
        },
        /** Class name of the skeleton loaders container when the toggle icon is left-aligned. */
        skeletonLoadersContainerLeftAligned: {
            columnGap: hawkinsVariables.spacings.space2,
            gridTemplate: "\n        \"toggle-icon title\" min-content\n        \". subtitle\" min-content\n        / min-content 1fr\n      ",
            '&$skeletonLoadersContainerCompact': { columnGap: hawkinsVariables.spacings.space3 },
        },
        /** Class name of the skeleton loaders container when the component is not compact or large. */
        skeletonLoadersContainerRegular: {
            minHeight: minHeightRegular,
            rowGap: hawkinsVariables.spacingValues.space0 / 2,
        },
        /** Class name of the skeleton loaders container when the component has no subtitle. */
        skeletonLoadersContainerNoSubtitle: {
            rowGap: 0,
        },
        /** Class name of the skeleton loaders container when the toggle icon is hidden. */
        skeletonLoadersContainerToggleIconHidden: {
            gridTemplate: "\n        \"title\" min-content\n        \"subtitle\" min-content\n        / 1fr\n      ",
        },
        /** Class name of the element containing the subtitle skeleton loader. */
        subtitleSkeletonLoaderContainer: {
            gridArea: 'subtitle',
            height: (hawkinsVariables.sizingValues.size0 * 13) / 8,
        },
        /** Class name of the subtitle skeleton loader container when the component is compact. */
        subtitleSkeletonLoaderContainerCompact: {
            height: (hawkinsVariables.sizingValues.size0 * 5) / 4,
        },
        /** Class name of the element containing the title skeleton loader. */
        titleSkeletonLoaderContainer: {
            gridArea: 'title',
            height: hawkinsVariables.sizings.size1,
        },
        /** Class name of the title skeleton loader container when the component is large. */
        titleSkeletonLoaderContainerLarge: {
            height: (hawkinsVariables.sizingValues.size1 * 5) / 4,
        },
        /** Class name of the toggle icon. */
        toggleIcon: {
            color: hawkins.icon.fillRegular,
            transition: "transform ".concat(COLLAPSE_ANIMATION_DURATION, "ms ").concat(COLLAPSE_ANIMATION_TIMING_FN),
        },
        /** Class name of the toggle icon button. */
        toggleIconButton: {
            // HACK(style): CSS override specificity.
            '&.Hawkins-Button': { backgroundColor: 'transparent' },
        },
        /** Class name of the toggle icon button when the component is compact. */
        toggleIconButtonCompact: {
            // HACK(style): CSS override specificity.
            '&.Hawkins-Button': { padding: (hawkinsVariables.spacingValues.space0 * 3) / 4 },
        },
        /** Class name of the toggle icon button when the component is not disabled. */
        toggleIconButtonEnabled: {
            // HACK(style): CSS override specificity.
            '&.Hawkins-Button': (_d = {},
                _d[hawkinsUtils.getHoveredElementSelector('&')] = { backgroundColor: hawkins.state.backgroundColorHover },
                _d),
        },
        /** Class name of the element containing the toggle icon or toggle icon button. */
        toggleIconContainer: {
            alignItems: 'center',
            display: 'flex',
            height: toggleIconContainerDim,
            justifyContent: 'center',
            width: toggleIconContainerDim,
        },
        /** Class name of the toggle icon container when the component is compact. */
        toggleIconContainerCompact: {
            height: toggleIconContainerDimCompact,
            width: toggleIconContainerDimCompact,
        },
        /** Class name of the toggle icon container when the toggle icon is right-aligned. */
        toggleIconContainerRightAligned: {
            marginLeft: 'auto',
        },
        /** Class name of the element containing the toggle icon skeleton loader. */
        toggleIconSkeletonLoaderContainer: {
            gridArea: 'toggle-icon',
            height: toggleIconSkeletonLoaderDim,
            width: toggleIconSkeletonLoaderDim,
        },
        /** Class name of the toggle icon skeleton loader container when the component is compact. */
        toggleIconSkeletonLoaderContainerCompact: {
            height: toggleIconSkeletonLoaderDimCompact,
            width: toggleIconSkeletonLoaderDimCompact,
        },
    });
}
export var useAccordionSummaryStyles = makeStyles(accordionSummaryStyles, { name: 'Hawkins-AccordionSummary' });
